import { CalendarCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useCycleSettings } from '@/hooks/useCycleSettings';
import { toISODate } from '@/lib/cycle';

interface CycleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function TrackingToggle({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-border bg-muted/40 p-4">
      <div className="space-y-1">
        <label htmlFor={id} className="text-base font-semibold leading-snug">
          {label}
        </label>
        <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="mt-1 scale-125"
      />
    </div>
  );
}

/**
 * Manual cycle setup: last period start, expected cycle length, which
 * dimensions to track, and whether the user gets painful flare-ups at all.
 */
export function CycleSettingsDialog({ open, onOpenChange }: CycleSettingsDialogProps) {
  const { settings, update } = useCycleSettings();
  const todayISO = toISODate(new Date());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Cycle settings</DialogTitle>
          <DialogDescription className="text-base">
            Tell Better Me when to expect your period, and choose what you want to track.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Last period start */}
          <div className="space-y-2">
            <label htmlFor="last-period" className="text-base font-semibold">
              Last period started on
            </label>
            <Input
              id="last-period"
              type="date"
              max={todayISO}
              value={settings.lastPeriodStart ?? ''}
              onChange={(e) => update({ lastPeriodStart: e.target.value || undefined })}
              className="h-14 rounded-xl text-lg"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => update({ lastPeriodStart: todayISO })}
              className="h-11 w-full rounded-xl border-blush-strong/40 bg-blush/50 font-semibold text-blush-foreground hover:bg-blush"
            >
              <CalendarCheck className="size-4" aria-hidden />
              My period started today
            </Button>
          </div>

          {/* Cycle length */}
          <div className="space-y-2">
            <label htmlFor="cycle-length" className="text-base font-semibold">
              Cycle length (days)
            </label>
            <Input
              id="cycle-length"
              type="number"
              inputMode="numeric"
              min={15}
              max={90}
              placeholder="Auto-detect from my logs"
              value={settings.cycleLength ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                update({ cycleLength: value === '' ? undefined : Number(value) });
              }}
              className="h-14 rounded-xl text-lg"
            />
            <p className="text-xs text-muted-foreground">
              Typical cycles are 21–35 days. Leave empty and Better Me will average your logged
              periods.
            </p>
          </div>

          <Separator />

          {/* Tracking dimensions */}
          <div className="space-y-3">
            <h3 className="text-base font-bold">What do you track?</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Turn off anything that doesn&apos;t apply to you — hidden fields disappear from the
              daily log but your existing data is kept.
            </p>

            <TrackingToggle
              id="track-flow"
              label="Flow"
              description="Track light / medium / heavy days. Off: predictions use your manual dates above."
              checked={settings.trackFlow}
              onCheckedChange={(checked) => update({ trackFlow: checked })}
            />
            <TrackingToggle
              id="track-pain"
              label="Pain severity"
              description="Log daily pain levels (0–10). Not everyone has painful cycles."
              checked={settings.trackPain}
              onCheckedChange={(checked) => update({ trackPain: checked })}
            />
            <TrackingToggle
              id="track-vitals"
              label="Heart rate & HRV"
              description="Sync wearable vitals (simulated Health Connect / HealthKit)."
              checked={settings.trackVitals}
              onCheckedChange={(checked) => update({ trackVitals: checked })}
            />
            <TrackingToggle
              id="track-flareups"
              label="Painful flare-ups"
              description="Off: predictions focus on your period dates only — no pain warnings or flare-up markers."
              checked={settings.trackFlareUps}
              onCheckedChange={(checked) => update({ trackFlareUps: checked })}
            />
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="h-13 w-full rounded-2xl text-lg font-bold"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
